import clsx from 'clsx';
import React, { useRef } from 'react';
import Modal from 'react-modal';
import Scrollbar from 'smooth-scrollbar';
import PageTitle, { IPageTitleProps } from '../PageTitle/PageTitle';
import { useFullModalSwipeBack } from '@/utils/hooks/useSwipeBack';
import { SwipeBackParam } from '@yanolja-org/ya-native/dist/interfaces';

import styles from './FullScreenModal.module.scss';

export type TModalTitleProps = Omit<IPageTitleProps, 'children' | 'elRef'> & {
  content: React.ReactNode;
  fixed?: boolean;
};

interface IFullScreenModalProps {
  isOpen: boolean;
  /** 참이면 smooth-scroll 적용 */
  scrollable?: boolean;
  title?: TModalTitleProps | string;
  /** title 하단 border 노출 여부 */
  hasTitleBottomBorder?: boolean;
  contentBodyClass?: string;
  /**
   * 스크롤 컨텍스트 바깥에 그려낼 자식 노드.
   *
   * scrollable=true인데 전체화면 모달 등을 하나 더 포함해야 할 때 유용.
   * e.g. PlaceDetailGallery
   **/
  outerChildren?: React.ReactNode;
  modalStyleClass?: string;
  /** IOS 스와이프백 옵션 (default - fixed : swipeback 미허용) */
  swipeBackOption?: SwipeBackParam;
  onAfterOpen?: () => void;
  onRequestClose?: () => void;
  'data-test'?: string;
}

const FullScreenModal: React.FC<IFullScreenModalProps> = ({
  isOpen,
  scrollable,
  title,
  hasTitleBottomBorder = true,
  contentBodyClass,
  outerChildren,
  modalStyleClass,
  swipeBackOption,
  onAfterOpen,
  onRequestClose,
  children,
  'data-test': testID,
}) => {
  const contentBody = useRef<HTMLElement>(null);
  let scrollbar: Scrollbar;

  const initScrollbar = (): void => {
    if (contentBody.current) {
      scrollbar = Scrollbar.init(contentBody.current, {
        continuousScrolling: false,
      });
    }
  };
  const destroyScrollbar = (): void => {
    if (scrollbar) {
      scrollbar.destroy();
    }
  };

  const renderPageTitle = (): JSX.Element => {
    if (typeof title === 'string') {
      return (
        <PageTitle className={styles.title} hasBottomBorder={hasTitleBottomBorder}>
          {title}
        </PageTitle>
      );
    }
    return (
      <PageTitle className={styles.title} hasBottomBorder={hasTitleBottomBorder} {...title}>
        {title && title.content}
      </PageTitle>
    );
  };

  const onAfterOpenModal = (): void => {
    if (scrollable) {
      destroyScrollbar();
      initScrollbar();
    }
    if (onAfterOpen) {
      onAfterOpen();
    }
  };

  useFullModalSwipeBack(isOpen, swipeBackOption);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      onAfterOpen={onAfterOpenModal}
      closeTimeoutMS={300}
      ariaHideApp={false}
      className={{
        afterOpen: styles.contentAfterOpen,
        base: clsx(styles.content, title && styles.contentWithTitle, modalStyleClass),
        beforeClose: styles.contentBeforeClose,
      }}
      overlayClassName={{
        afterOpen: styles.contentAfterOpen,
        base: clsx(styles.overlay),
        beforeClose: styles.contentBeforeClose,
      }}
    >
      {title && renderPageTitle()}
      {outerChildren && <section>{outerChildren}</section>}
      <section ref={contentBody} className={clsx(styles.contentBody, contentBodyClass)} data-test={testID}>
        {children}
      </section>
    </Modal>
  );
};

export default FullScreenModal;
